﻿import { scroller } from '../../utilities/scroller';
import { eventBus } from '../../foundation/eventBus';
import { shouldBeValidated, isRequired, hasPattern, hasAttribute, matchesPattern, hasMatching, matchingText, isGroupRequired, hasMinimumOptionsChecked, disableSubmitButton } from '../../foundation/form/validation';
import { Field } from './field';

export const events = {
    formSubmitted: 'formSubmitted',
    validate: 'validate',
};

export const enableFormSubmission = () => {

    const Form = function (form) {
        form.setAttribute('novalidate', '');

        let isSubmitted = false;

        // Check if input needs validating or not
        const shouldSkipValidation = (field) => !field || !field.element;

        function getFields() {
            const allFields = [...form.querySelectorAll('input, textarea, select')];

            return allFields.filter(field => shouldBeValidated(field))
            .map(field => Field(field));
        }

        const isValidField = field => {
            if (shouldSkipValidation(field)) {
                return;
            }

            let isInvalid;

            const element = field.element;

            if (hasAttribute(element, 'skip-validation')) {
                return true;
            } else if (element.type === 'checkbox' || element.type === 'radio') {
                isInvalid = (isRequired(element) && !element.checked) || (isGroupRequired(element) && !hasMinimumOptionsChecked(element));
            } else if(hasAttribute(element, 'pattern-only-validation')) {
                if(isRequired(element)) {
                    isInvalid = !element.value.trim() || (hasPattern(element) && !matchesPattern(element));
                } else {
                    isInvalid = element.value.trim() ? (hasPattern(element) && !matchesPattern(element)) : false;
                }
            } else {
                isInvalid = (isRequired(element) && !element.value.trim()) || (hasPattern(element) && !matchesPattern(element)) || (hasMatching(element) && !matchingText(element));
            }

            return !isInvalid;
        };

        const isValidForm = () => getFields().every(isValidField);

        const validateField = field => {
            if (shouldSkipValidation(field)) {
                return;
            }

            const isValid = isValidField(field);

            if (isValid) {
                field.hideError();
            } else {
                field.showError();
                field.setEagerValidation();
            }

            return isValid;
        };

        const validateForm = (options = {}) => {
            const isValid = isValidForm();

            if (!options.ignoreErrors) {
                getFields().forEach(field => {
                    validateField(field);
                });
            }

            return isValid;
        };

        function submitEvent(event) {
            isSubmitted = true;
            const isValidForm = validateForm();

            eventBus.publish(events.formSubmitted, { isValidForm: isValidForm, id : form.id, event: event });

            if (!isValidForm) {
                event.preventDefault();
                const field = getFields().find(field => !isValidField(field));
                const element = field.formGroup;

                const paddingTop = 8;

                if (element && !scroller.isScrolledIntoView(element, paddingTop)) {
                    scroller.scrollToElement(element, field.focus, paddingTop);
                } else {
                    field.focus();
                }
            } else {
                var button = document.querySelector("[data-submit-disable]");
                disableSubmitButton(button);
            }
        }

        const bindEvents = () => {
            getFields().forEach(field => {
                if (shouldSkipValidation(field)) {
                    return;
                }

                const element = field.element;
                const eventType = ['checkbox', 'radio', 'select-one'].includes(element.type) ? 'change' : 'blur';

                const handler = event => {
                    const shouldRun = ((event.type === 'blur' || event.type === 'change') && !field.isEagerValidationEnabled() && (isSubmitted || Boolean(element.value))) || field.isEagerValidationEnabled();

                    if (shouldRun) {
                        validateField(field);
                    }

                    if (eventType === 'blur' && element && element.type === 'date' && element.value && element.max) {
                        let date = new Date(element.value).toISOString().slice(0, 10);
                        let today = new Date().toISOString().slice(0, 10);
                        if (date > today) {
                            element.value = today;
                        }
                    }
                };

                element.addEventListener(eventType, handler);

                if (eventType === 'blur') {
                    element.addEventListener('input', handler);
                }
            });

            form.onsubmit = submitEvent;
        };

        return {
            init() {
                bindEvents();
                eventBus.subscribe(events.validate, validateForm);
            }
        };
    };

    const init = component => {
        const form = new Form(component);
        form.init();
    };

    return {
        init: init,
        selector: '[data-component="form"] .js-form, .form .js-form'
    };
};